import { useLearnEnabled, LearnPaused } from '@/lib/learnFeature';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import SEO from '@/components/SEO';
import { useLanguage } from '@/context/LanguageContext';
import { API_BASE_URL as API, BACKEND_BASE_URL as BACKEND_URL } from '@/lib/apiBase';

const trustDisclosure =
  'This curriculum was generated by AI and reviewed by the Kalamanch team. It is a structured practice guide, not a substitute for training under a qualified guru or teacher.';
const guideNotice = 'This is a structured self-practice guide. It does not include AI feedback or correction.';
const comingSoonNotice = 'AI feedback coming soon. This curriculum is read-only for now and does not accept submissions.';
const coachedHoursNotice = 'Hours are estimated practice-and-feedback time, not reading time.';

const levelLabels = {
  absolute_beginner: 'Absolute beginner',
  some_exposure: 'Some exposure',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

const disciplineLabels = {
  dance: 'Dance',
  music_vocal: 'Music - Vocal',
  music_instrumental: 'Music - Instrumental',
  visual_art: 'Visual Art',
  theatre: 'Theatre',
  voice: 'Voice',
  literary: 'Literary',
  craft: 'Craft',
  other: 'Other',
};

const learnCopy = {
  en: {
    seoTitle: 'Kalamanch Learn | AI-reviewed practice guides for Indian artists',
    seoDescription: 'Browse AI-generated, human-reviewed Kalamanch Learn curricula for Indian artists. Build your foundation with structured lessons and self-paced practice.',
    heroEyebrow: 'Kalamanch Learn',
    heroTitle: 'Build your foundation.',
    heroBody: 'Levelled practice guides for artists, writers, performers, and creators. Kalamanch Learn builds your foundation. Your guru builds your art.',
    browseCourses: 'Browse courses',
    searchPlaceholder: 'Search Bharatanatyam, voice-over, poetry, tabla...',
    artForms: 'art forms',
    generatedPublished: 'AI generated, human published',
    artFormBrowser: 'Art form browser',
    publishedCurricula: 'Published curricula',
    noCurriculum: 'No published curriculum yet for this selection.',
    englishFallback: 'English version shown because Hindi version is not published yet.',
    previewSyllabus: 'Preview syllabus',
    learnerDashboard: 'Learner dashboard',
    continueWhereLeft: 'Continue where you left off',
    dashboardHint: 'All enrolled Learn plans are saved here. Open any plan and continue your next lesson.',
    plans: 'plan(s)',
    learnPlan: 'Learn plan',
    done: 'done',
    progressWillAppear: 'Progress will appear after the plan opens.',
    allComplete: 'All listed lessons are complete.',
    continuePlan: 'Continue plan',
    curriculumPreview: 'Curriculum preview',
    enrollTitle: 'Enroll in this guide',
    hoursPerWeek: 'Hours per week',
    enrolling: 'Enrolling...',
    enroll: 'Enroll',
    optionalGoal: 'Optional goal, e.g. build confidence for stage performance in 6 months',
    myPlan: 'My personalized plan',
    level: 'Level',
    complete: 'Complete',
    markComplete: 'Mark complete',
    freeContinueTitle: 'Ready for Level 2?',
    freeContinueBody: 'Create a free Kalamanch account to continue with Level 2 onwards, save your personal plan, and track progress.',
    signUpFree: 'Sign up free to continue',
    levelLockedHint: 'Lesson details unlock after free signup and enrollment. The full Level 1 remains open above.',
    loading: 'Loading...',
    notSet: 'Not set',
    totalHours: 'Total hours',
    weeks: 'Weeks',
    levels: 'Levels',
    startDate: 'Start date',
    endDate: 'End date',
    weeklyCommitment: 'Weekly commitment',
  },
  hi: {
    seoTitle: 'कलामंच लर्न | भारतीय कलाकारों के लिए अभ्यास मार्गदर्शिका',
    seoDescription: 'कलामंच लर्न पर AI द्वारा बनाए और मानव समीक्षा वाले पाठ्यक्रम देखें। अपनी बुनियाद को व्यवस्थित अभ्यास से मजबूत करें।',
    heroEyebrow: 'कलामंच लर्न',
    heroTitle: 'अपनी बुनियाद मजबूत करें।',
    heroBody: 'कलाकारों, लेखकों, परफॉर्मर्स और क्रिएटर्स के लिए स्तरवार अभ्यास मार्गदर्शिका। कलामंच लर्न आपकी नींव बनाता है। आपका गुरु आपकी कला बनाता है।',
    browseCourses: 'कोर्स देखें',
    searchPlaceholder: 'भरतनाट्यम, वॉइस-ओवर, कविता, तबला खोजें...',
    artForms: 'कला विधाएं',
    generatedPublished: 'AI द्वारा बनाया, मानव द्वारा प्रकाशित',
    artFormBrowser: 'कला विधा ब्राउज़र',
    publishedCurricula: 'प्रकाशित पाठ्यक्रम',
    noCurriculum: 'इस चयन के लिए अभी कोई प्रकाशित पाठ्यक्रम नहीं है।',
    englishFallback: 'हिंदी संस्करण अभी प्रकाशित नहीं है, इसलिए अंग्रेजी संस्करण दिखाया जा रहा है।',
    previewSyllabus: 'सिलेबस देखें',
    learnerDashboard: 'लर्नर डैशबोर्ड',
    continueWhereLeft: 'जहां छोड़ा था वहां से जारी रखें',
    dashboardHint: 'आपके सभी Learn प्लान यहां सुरक्षित हैं। कोई भी प्लान खोलकर अगला पाठ जारी रखें।',
    plans: 'प्लान',
    learnPlan: 'लर्न प्लान',
    done: 'पूरा',
    progressWillAppear: 'प्लान खुलने के बाद प्रगति यहां दिखेगी।',
    allComplete: 'सभी सूचीबद्ध पाठ पूरे हो चुके हैं।',
    continuePlan: 'प्लान जारी रखें',
    curriculumPreview: 'पाठ्यक्रम प्रीव्यू',
    enrollTitle: 'इस गाइड में नामांकन करें',
    hoursPerWeek: 'प्रति सप्ताह घंटे',
    enrolling: 'नामांकन हो रहा है...',
    enroll: 'नामांकन करें',
    optionalGoal: 'वैकल्पिक लक्ष्य, जैसे 6 महीने में मंच आत्मविश्वास बनाना',
    myPlan: 'मेरा व्यक्तिगत प्लान',
    level: 'स्तर',
    complete: 'पूरा',
    markComplete: 'पूरा मार्क करें',
    freeContinueTitle: 'Level 2 के लिए तैयार?',
    freeContinueBody: 'Level 2 से आगे जारी रखने, अपना व्यक्तिगत प्लान सुरक्षित करने और प्रगति ट्रैक करने के लिए मुफ्त Kalamanch अकाउंट बनाएं।',
    signUpFree: 'जारी रखने के लिए मुफ्त साइन अप करें',
    levelLockedHint: 'मुफ्त साइनअप और नामांकन के बाद पाठ खुलेंगे। पूरा Level 1 ऊपर खुला है।',
    loading: 'लोड हो रहा है...',
    notSet: 'सेट नहीं',
    totalHours: 'कुल घंटे',
    weeks: 'सप्ताह',
    levels: 'स्तर',
    startDate: 'शुरू तारीख',
    endDate: 'अंतिम तारीख',
    weeklyCommitment: 'साप्ताहिक अभ्यास',
  },
};

const levelLabelsByLanguage = {
  en: levelLabels,
  hi: {
    absolute_beginner: 'बिल्कुल शुरुआती',
    some_exposure: 'थोड़ा अनुभव',
    intermediate: 'मध्यम स्तर',
    advanced: 'उन्नत',
  },
};

const markdownComponents = {
  h1: ({ children }) => <h1 className="mt-4 text-2xl font-black text-slate-950">{children}</h1>,
  h2: ({ children }) => <h2 className="mt-4 text-xl font-black text-slate-950">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-3 text-lg font-black text-slate-950">{children}</h3>,
  p: ({ children }) => <p className="mt-2 leading-7">{children}</p>,
  ul: ({ children }) => <ul className="mt-2 list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="mt-2 list-decimal space-y-1 pl-5">{children}</ol>,
  li: ({ children }) => <li className="leading-7">{children}</li>,
  strong: ({ children }) => <strong className="font-black text-slate-950">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
};

function MarkdownText({ children, className = '' }) {
  const value = String(children || '').trim();
  if (!value) return null;
  return (
    <div className={className}>
      <ReactMarkdown components={markdownComponents}>{value}</ReactMarkdown>
    </div>
  );
}

const safeDiagramSvg = (value) => {
  const svg = String(value || '').trim();
  if (!svg || !svg.startsWith('<svg') || !svg.endsWith('</svg>')) return '';
  if (/<script|javascript:|on\w+=/i.test(svg)) return '';
  return svg;
};

function LessonDiagram({ svg }) {
  const safeSvg = safeDiagramSvg(svg);
  if (!safeSvg) return null;
  return (
    <div
      className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-h-80 [&_svg]:max-w-full"
      aria-label="Lesson schematic diagram"
      dangerouslySetInnerHTML={{ __html: safeSvg }}
    />
  );
}

const formatTime = (seconds) => {
  const value = Math.max(0, Number(seconds || 0));
  const mins = Math.floor(value / 60);
  const secs = Math.floor(value % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const issueKey = (type, index) => `${type}-${index}`;

function MarkedScript({ targetText, metrics, onJump }) {
  const words = String(targetText || '').split(/\s+/).filter(Boolean);
  const omissions = metrics?.omissions || [];
  const missedPauses = metrics?.missed_required_pauses || [];
  const omissionByIndex = omissions.reduce((map, item, index) => {
    map[item.reference_index] = { ...item, issueIndex: index };
    return map;
  }, {});
  const pausesByIndex = missedPauses.reduce((map, item, index) => {
    map[item.after_word_index] = map[item.after_word_index] || [];
    map[item.after_word_index].push({ ...item, issueIndex: index });
    return map;
  }, {});
  const timedExtras = [
    ...(metrics?.filler_instances || []).map((item, index) => ({ ...item, type: 'filler', label: `Filler: ${item.word}`, key: issueKey('filler', index) })),
    ...(metrics?.insertions || []).map((item, index) => ({ ...item, type: 'insertion', label: `Extra word: ${item.word}`, key: issueKey('insertion', index) })),
  ].filter((item) => item.timestamp !== undefined && item.timestamp !== null);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Marked script</p>
      <p className="mt-3 text-base leading-9 text-slate-800">
        {words.map((word, index) => {
          const omitted = omissionByIndex[index];
          return (
            <span key={`${word}-${index}`} className="mr-1 inline">
              <button
                type="button"
                onClick={() => omitted?.timestamp !== undefined && onJump(omitted.timestamp)}
                className={omitted ? 'rounded bg-red-50 px-1 text-red-700 line-through decoration-2' : ''}
                title={omitted ? `Omitted near ${formatTime(omitted.timestamp)}` : ''}
              >
                {word}
              </button>
              {(pausesByIndex[index + 1] || []).map((pause) => (
                <button
                  key={pause.issueIndex}
                  type="button"
                  onClick={() => onJump(pause.timestamp)}
                  className="mx-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-black text-amber-800"
                  title={`Missed pause near ${formatTime(pause.timestamp)}`}
                >
                  pause
                </button>
              ))}
            </span>
          );
        })}
      </p>
      {timedExtras.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {timedExtras.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onJump(item.timestamp)}
              className={`rounded-full px-3 py-1 text-xs font-black ${item.type === 'filler' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-[#C45A00]'}`}
            >
              {item.label} at {formatTime(item.timestamp)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VoicePracticePanel({ lesson, selectedPlan, lessonTargets, activeLanguage }) {
  const [targetId, setTargetId] = useState('');
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [level, setLevel] = useState(0);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentRequired, setConsentRequired] = useState(true);
  const [consentLoading, setConsentLoading] = useState(true);
  const [hasPlayedBack, setHasPlayedBack] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const meterFrameRef = useRef(null);

  const targets = useMemo(
    () => (lessonTargets || []).filter((target) => target.lesson_id === lesson?.id),
    [lesson?.id, lessonTargets],
  );
  const selectedTarget = useMemo(() => targets.find((item) => item.id === targetId) || targets[0] || null, [targets, targetId]);

  useEffect(() => {
    if (!targetId && targets[0]?.id) setTargetId(targets[0].id);
  }, [targetId, targets]);

  const loadAttempts = useCallback(async () => {
    if (!lesson?.id) return;
    try {
      const response = await axios.get(`${API}/learn/me/lessons/${encodeURIComponent(lesson.id)}/attempts`, { withCredentials: true });
      setAttempts(response.data?.attempts || []);
    } catch (_ignored) {
      setAttempts([]);
    }
  }, [lesson?.id]);

  useEffect(() => {
    loadAttempts();
  }, [loadAttempts]);

  useEffect(() => {
    let mounted = true;
    const loadConsent = async () => {
      setConsentLoading(true);
      try {
        const response = await axios.get(`${API}/learn/audio-consent`, { withCredentials: true });
        if (!mounted) return;
        const accepted = Boolean(response.data?.accepted);
        setConsentRequired(!accepted);
        setConsentAccepted(accepted);
      } catch (_ignored) {
        if (mounted) {
          setConsentRequired(true);
          setConsentAccepted(false);
        }
      } finally {
        if (mounted) setConsentLoading(false);
      }
    };
    loadConsent();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (meterFrameRef.current) cancelAnimationFrame(meterFrameRef.current);
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
    };
  }, [audioUrl]);

  const stopMeter = () => {
    if (meterFrameRef.current) cancelAnimationFrame(meterFrameRef.current);
    meterFrameRef.current = null;
    setLevel(0);
  };

  const startRecording = async () => {
    setError('');
    setResult(null);
    setStatusText('');
    setHasPlayedBack(false);
    if (!selectedTarget) {
      setError('No voice-over script is available yet.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((sum, value) => sum + value, 0) / Math.max(1, data.length);
        setLevel(Math.min(100, Math.round((avg / 160) * 100)));
        meterFrameRef.current = requestAnimationFrame(tick);
      };
      tick();
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stopMeter();
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (_err) {
      setError('Microphone access is needed to record your read.');
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop?.();
    setRecording(false);
  };

  const jumpAudio = (seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Number(seconds || 0));
    audioRef.current.play().catch(() => {});
  };

  const pollSubmission = async (submissionId) => {
    for (let attempt = 0; attempt < 24; attempt += 1) {
      const response = await axios.get(`${API}/learn/submissions/${encodeURIComponent(submissionId)}`, { withCredentials: true });
      const payload = response.data || {};
      setResult(payload);
      const status = payload.submission?.status;
      if (status && !['processing'].includes(status)) {
        if (['analysis_error', 'failed'].includes(status)) {
          setStatusText('');
          setError(payload.submission?.error || "We couldn't analyse this recording. Please try again.");
        } else {
          setStatusText(status === 'scored' ? 'Analysis complete.' : 'Recording needs a cleaner retake.');
        }
        await loadAttempts();
        return;
      }
      setStatusText('Analysing your recording: transcribing, checking timing, fillers, pace, and delivery...');
      await new Promise((resolve) => setTimeout(resolve, 2500));
    }
    setStatusText('Still analysing. Please check this attempt again in a moment.');
  };

  const submitRecording = async () => {
    setError('');
    setStatusText('');
    if (!audioBlob) {
      setError('Record and play back your read before submitting.');
      return;
    }
    if (!hasPlayedBack) {
      setError('Please play back your recording once before submitting.');
      return;
    }
    if (!consentAccepted) {
      setError('Please accept the audio consent before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('lesson_target_id', selectedTarget.id);
      form.append('learner_curriculum_id', selectedPlan?.id || '');
      form.append('lesson_id', lesson?.id || '');
      form.append('consent_audio_processing', 'true');
      form.append('file', audioBlob, `kalamanch-learn-${Date.now()}.webm`);
      const response = await axios.post(`${API}/learn/submissions`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const submissionId = response.data?.submission?.id;
      setResult(response.data || null);
      setConsentRequired(false);
      setConsentAccepted(true);
      setStatusText('Recording submitted. Analysing now...');
      if (submissionId) await pollSubmission(submissionId);
      await loadAttempts();
    } catch (submitError) {
      const detail = submitError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Could not submit this recording.');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRecording = async () => {
    const submissionId = result?.submission?.id;
    if (!submissionId) return;
    try {
      await axios.delete(`${API}/learn/submissions/${encodeURIComponent(submissionId)}/audio`, { withCredentials: true });
      setResult((prev) => ({ ...prev, submission: { ...prev.submission, audio_deleted: true, audio_playback_url: '' } }));
      await loadAttempts();
      setStatusText('Recording deleted. Your learning metrics remain saved.');
    } catch (deleteError) {
      setError(deleteError.response?.data?.detail || 'Could not delete this recording.');
    }
  };

  const deleteAttemptAudio = async (submissionId) => {
    if (!submissionId) return;
    try {
      await axios.delete(`${API}/learn/submissions/${encodeURIComponent(submissionId)}/audio`, { withCredentials: true });
      if (result?.submission?.id === submissionId) {
        setResult((prev) => ({ ...prev, submission: { ...prev.submission, audio_deleted: true, audio_playback_url: '' } }));
      }
      await loadAttempts();
      setStatusText('Recording deleted. Your learning metrics remain saved.');
    } catch (deleteError) {
      setError(deleteError.response?.data?.detail || 'Could not delete this recording.');
    }
  };

  if (!selectedTarget) return null;

  const metrics = result?.analysis?.metrics || {};
  const feedback = result?.feedback?.feedback || {};
  const score = result?.analysis?.overall_score;
  const resultStatus = result?.submission?.status || '';
  const analysisUnavailable = ['analysis_error', 'failed'].includes(resultStatus);
  const analysisErrorMessage = result?.submission?.error || "We couldn't analyse this recording. Please try again.";
  const retainedUntil = result?.submission?.audio_expires_at;
  const playbackUrlFor = (submission) => (submission?.audio_playback_url ? `${BACKEND_URL}${submission.audio_playback_url}` : '');

  return (
    <section className="mt-4 rounded-2xl border border-emerald-100 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">Voice practice</p>
          <h4 className="mt-1 text-lg font-black text-slate-950">{selectedTarget.title}</h4>
        </div>
        {targets.length > 1 && (
          <select className="rounded-2xl border border-slate-200 px-3 py-2 text-sm font-bold" value={targetId} onChange={(event) => setTargetId(event.target.value)}>
            {targets.map((target) => <option key={target.id} value={target.id}>{target.title}</option>)}
          </select>
        )}
      </div>
      <div className="mt-3 rounded-2xl bg-slate-50 p-4 text-lg font-semibold leading-8 text-slate-800">
        {selectedTarget.target_text}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <div className="h-3 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${level}%` }} />
          </div>
          <p className="mt-1 text-xs font-bold text-slate-500">Live voice level</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!recording ? (
            <button type="button" onClick={startRecording} className="rounded-full bg-[#FF6F00] px-4 py-2 text-sm font-black text-white">Record</button>
          ) : (
            <button type="button" onClick={stopRecording} className="rounded-full bg-red-600 px-4 py-2 text-sm font-black text-white">Stop</button>
          )}
          <button type="button" onClick={submitRecording} disabled={submitting || !audioBlob || !hasPlayedBack || consentLoading || !consentAccepted} className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-black text-white disabled:opacity-50">
            {submitting ? 'Submitting...' : 'Submit for feedback'}
          </button>
        </div>
      </div>
      {audioUrl && (
        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-3">
          <p className="text-sm font-black text-slate-700">Playback before submit</p>
          <audio ref={audioRef} className="mt-2 w-full" controls src={audioUrl} onPlay={() => setHasPlayedBack(true)} />
          <p className={`mt-2 text-xs font-bold ${hasPlayedBack ? 'text-emerald-700' : 'text-slate-500'}`}>
            {hasPlayedBack ? 'Playback checked. You can submit when ready.' : 'Listen once before submitting so you can catch obvious mistakes.'}
          </p>
        </div>
      )}
      {consentLoading ? (
        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-3 text-sm font-semibold text-slate-600">Checking audio consent...</div>
      ) : consentRequired ? (
        <label className="mt-4 flex gap-3 rounded-2xl border border-orange-100 bg-orange-50 p-3 text-sm font-semibold text-slate-700">
          <input type="checkbox" className="mt-1" checked={consentAccepted} onChange={(event) => setConsentAccepted(event.target.checked)} />
          <span>
            I consent to Kalamanch storing this lesson audio for up to 90 days for playback, timestamped feedback, and learning review. I understand it is sent to OpenAI for transcription, and I can delete the recording.
          </span>
        </label>
      ) : (
        <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-3 text-sm font-bold text-emerald-800">Audio consent is already on file for this account.</div>
      )}
      {statusText && <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">{statusText}</div>}
      {error && <div className="mt-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{error}</div>}
      {analysisUnavailable && !error && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-900">
          {analysisErrorMessage}
        </div>
      )}
      {result?.analysis && !analysisUnavailable && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="rounded-2xl bg-emerald-50 p-3"><p className="text-xs font-black uppercase text-emerald-700">Score</p><p className="text-2xl font-black">{score ?? '-'}</p></div>
            <div className="rounded-2xl bg-slate-50 p-3"><p className="text-xs font-black uppercase text-slate-500">WPM</p><p className="text-xl font-black">{metrics.wpm_overall ?? '-'}</p></div>
            <div className="rounded-2xl bg-slate-50 p-3"><p className="text-xs font-black uppercase text-slate-500">Fillers</p><p className="text-xl font-black">{metrics.filler_count ?? 0}</p></div>
            <div className="rounded-2xl bg-slate-50 p-3"><p className="text-xs font-black uppercase text-slate-500">Confidence</p><p className="text-xl font-black">{Math.round((result.analysis.confidence || 0) * 100)}%</p></div>
          </div>
          {result.analysis.status === 'rejected_quality' ? (
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 font-bold text-amber-800">{result.analysis.quality_message || 'The recording was too unclear to analyse. Please try again in a quieter place.'}</div>
          ) : (
            <>
              <MarkedScript targetText={selectedTarget.target_text} metrics={metrics} onJump={jumpAudio} />
              <div className="rounded-2xl border border-purple-100 bg-purple-50 p-4 text-sm font-bold text-purple-900">
                Feedback is generated automatically by analysing your recording against the script. It measures accuracy, pace, and delivery; it is not a substitute for a coach&apos;s ear.
              </div>
              {feedback.summary_md && <MarkdownText className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">{feedback.summary_md}</MarkdownText>}
              {Array.isArray(feedback.top_corrections) && feedback.top_corrections.length > 0 && (
                <div className="grid gap-3 md:grid-cols-3">
                  {feedback.top_corrections.map((item, index) => (
                    <div key={`${item.title}-${index}`} className="rounded-2xl border border-slate-100 bg-white p-3">
                      <p className="font-black text-slate-950">{item.title || `Correction ${index + 1}`}</p>
                      <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">{item.detail || item}</p>
                    </div>
                  ))}
                </div>
              )}
              {feedback.next_drill && <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-900">Next drill: {feedback.next_drill}</div>}
            </>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-slate-50 p-3 text-sm font-bold text-slate-600">
            <span>{retainedUntil && !result?.submission?.audio_deleted ? `Recording kept until ${formatDate(retainedUntil.slice(0, 10), activeLanguage)}.` : 'Recording not available.'}</span>
            {!result?.submission?.audio_deleted && <button type="button" onClick={deleteRecording} className="rounded-full border border-red-200 px-4 py-2 text-red-700">Delete recording</button>}
          </div>
        </div>
      )}
      {attempts.length > 0 && (
        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Past attempts</p>
              <p className="mt-1 text-sm font-semibold text-slate-600">Re-listen while the recording is retained. Metrics stay saved after audio deletion.</p>
            </div>
            <button type="button" onClick={loadAttempts} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-700">Refresh</button>
          </div>
          <div className="mt-3 space-y-3">
            {attempts.slice(0, 5).map((attempt) => {
              const submission = attempt.submission || {};
              const attemptScore = attempt.analysis?.overall_score;
              const attemptStatus = submission.status || attempt.analysis?.status || 'processing';
              const attemptAnalysisUnavailable = ['analysis_error', 'failed'].includes(attemptStatus);
              const attemptStatusLabel = attemptAnalysisUnavailable ? 'Analysis unavailable' : attemptStatus;
              const retainedAudioUrl = playbackUrlFor(submission);
              return (
                <div key={submission.id} className="rounded-2xl border border-white bg-white p-3 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-black text-slate-950">{formatDate((submission.created_at || '').slice(0, 10), activeLanguage)} · {attemptStatusLabel}</p>
                      {attemptAnalysisUnavailable ? (
                        <p className="text-sm font-semibold text-amber-700">{submission.error || "We couldn't analyse this recording. Please try again."}</p>
                      ) : (
                        <p className="text-sm font-semibold text-slate-600">Score {attemptScore ?? '-'} · WPM {attempt.analysis?.metrics?.wpm_overall ?? '-'}</p>
                      )}
                    </div>
                    {submission.audio_deleted ? (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-500">Audio deleted</span>
                    ) : (
                      <button type="button" onClick={() => deleteAttemptAudio(submission.id)} className="rounded-full border border-red-200 px-3 py-1 text-xs font-black text-red-700">Delete audio</button>
                    )}
                  </div>
                  {retainedAudioUrl && !submission.audio_deleted && (
                    <audio className="mt-3 w-full" controls src={retainedAudioUrl} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

const formatDate = (value, activeLanguage = 'en') => {
  if (!value) return learnCopy[activeLanguage]?.notSet || 'Not set';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(activeLanguage === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const shortRationale = (value) => {
  const cleaned = String(value || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const sentences = cleaned.split(/(?<=[.!?])\s+/).filter(Boolean).slice(0, 3);
  return sentences.join(' ') || cleaned.slice(0, 500);
};

const courseTier = (item) => ['coached', 'coming_soon'].includes(item?.course_tier) ? item.course_tier : 'guide';
const courseLabel = (item) => {
  if (courseTier(item) === 'coached') return 'Coached Course';
  if (courseTier(item) === 'coming_soon') return 'Course';
  return 'Practice Guide';
};
const courseNotice = (item) => {
  if (item?.tier_notice) return item.tier_notice;
  if (courseTier(item) === 'coached') return coachedHoursNotice;
  if (courseTier(item) === 'coming_soon') return comingSoonNotice;
  return guideNotice;
};
const courseBadge = (item) => courseTier(item) === 'coming_soon' ? 'AI feedback coming soon' : '';
const courseHoursLabel = (item) => courseTier(item) === 'coached' ? 'practice + feedback hours' : 'practice hours';

function ContentWatermark({ label }) {
  if (!label) return null;
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-3xl select-none">
      <div className="grid h-full min-h-[36rem] grid-cols-2 gap-x-20 gap-y-28 p-10 opacity-[0.055] sm:grid-cols-3">
        {Array.from({ length: 18 }, (_, index) => (
          <span key={`${label}-${index}`} className="-rotate-12 whitespace-nowrap text-xs font-black uppercase tracking-[0.18em] text-slate-950">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

const planSummaryItems = (plan, copy, activeLanguage = 'en') => {
  const adjustedPlan = Array.isArray(plan?.adjusted_plan) ? plan.adjusted_plan : [];
  const first = adjustedPlan[0] || {};
  const last = adjustedPlan[adjustedPlan.length - 1] || {};
  const totalWeeks = adjustedPlan.reduce((sum, level) => sum + Number(level.estimated_weeks || 0), 0);
  const notSet = copy?.notSet || 'Not set';
  const items = [
    [copy?.weeks || 'Weeks', totalWeeks ? `${totalWeeks}` : notSet],
    [copy?.levels || 'Levels', adjustedPlan.length ? `${adjustedPlan.length}` : `${plan?.curriculum_snapshot?.total_levels || notSet}`],
    [copy?.startDate || 'Start date', formatDate(first.target_start_date, activeLanguage)],
    [copy?.endDate || 'End date', formatDate(last.target_end_date, activeLanguage)],
    [copy?.weeklyCommitment || 'Weekly commitment', plan?.weekly_hours_committed ? `${plan.weekly_hours_committed} hrs/week` : notSet],
  ];
  if (courseTier(plan?.curriculum_snapshot) !== 'guide') {
    items.unshift([copy?.totalHours || 'Total hours', plan?.curriculum_snapshot?.estimated_total_hours ? `${plan.curriculum_snapshot.estimated_total_hours}` : notSet]);
  }
  return items;
};

const enrollmentProgressPercent = (item) => {
  const total = Number(item?.progress_summary?.total_lessons || 0);
  const completed = Number(item?.progress_summary?.completed_lessons || 0);
  if (!total) return 0;
  return Math.min(100, Math.round((completed / total) * 100));
};

function ComingSoonInterestPanel({ curriculum, isAuthenticated }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [interestError, setInterestError] = useState('');

  const submitInterest = async (event) => {
    event?.preventDefault?.();
    const cleanEmail = email.trim();
    if (!isAuthenticated && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setInterestError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setInterestError('');
    try {
      await axios.post(`${API}/learn/interest-signals`, {
        art_form_id: curriculum?.art_form_id,
        curriculum_id: curriculum?.id,
        ...(!isAuthenticated ? { email: cleanEmail } : {}),
      }, { withCredentials: true });
      setConfirmed(true);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      const validationMessage = Array.isArray(detail)
        ? detail.map((item) => item?.msg).filter(Boolean).join(' ')
        : '';
      setInterestError(
        (typeof detail === 'string' && detail)
        || (typeof detail?.message === 'string' && detail.message)
        || validationMessage
        || 'We could not save your interest. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 font-black text-emerald-800" role="status">
        We&apos;ll let you know.
      </div>
    );
  }

  return (
    <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-black text-slate-950">Want AI feedback for this course?</p>
      {!isAuthenticated && (
        <label className="mt-3 block text-sm font-bold text-slate-700">
          Email
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1.5 w-full rounded-xl border border-amber-200 bg-white px-4 py-3 font-medium outline-none focus:border-[#FF6F00]"
            placeholder="you@example.com"
          />
          <span className="mt-1.5 block text-xs font-medium text-slate-600">Used only to notify you when AI feedback is ready for this feature. It is not added to a marketing list.</span>
        </label>
      )}
      <button type="button" onClick={submitInterest} disabled={submitting} className="mt-3 rounded-full bg-[#FF6F00] px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60">
        {submitting ? 'Saving...' : 'Notify me when AI feedback is ready'}
      </button>
      {interestError && <p className="mt-3 text-sm font-bold text-red-700" role="alert">{interestError}</p>}
    </div>
  );
}

export default function Learn() {
  const enabled = useLearnEnabled();
  return enabled ? <LearnContent /> : <LearnPaused />;
}

function LearnContent() {
  const { language, setLanguage } = useLanguage();
  const activeLanguage = language === 'hi' ? 'hi' : 'en';
  const copy = learnCopy[activeLanguage];
  const activeLevelLabels = levelLabelsByLanguage[activeLanguage] || levelLabels;
  const [artForms, setArtForms] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [isLearnAuthenticated, setIsLearnAuthenticated] = useState(false);
  const [selectedArtFormId, setSelectedArtFormId] = useState('');
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planProgress, setPlanProgress] = useState([]);
  const [lessonTargets, setLessonTargets] = useState([]);
  const [contentWatermark, setContentWatermark] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [enrollMessage, setEnrollMessage] = useState('');
  const [enrollError, setEnrollError] = useState('');
  const [enrollForm, setEnrollForm] = useState({
    self_reported_level: 'absolute_beginner',
    weekly_hours_committed: 5,
    goal: '',
  });
  const learnRequestVersionRef = useRef(0);

  const clearPrivateLearnState = useCallback(() => {
    setIsLearnAuthenticated(false);
    setEnrollments([]);
    setSelectedPlan(null);
    setPlanProgress([]);
    setLessonTargets([]);
    setContentWatermark('');
  }, []);

  const loadLearn = useCallback(async () => {
    const requestVersion = ++learnRequestVersionRef.current;
    setLoading(true);
    setError('');
    try {
      const [formsResponse, curriculaResponse] = await Promise.all([
        axios.get(`${API}/learn/art-forms`),
        axios.get(`${API}/learn/curricula`, { params: { language: activeLanguage } }),
      ]);
      if (requestVersion !== learnRequestVersionRef.current) return;
      setArtForms(formsResponse.data?.art_forms || []);
      setCurricula(curriculaResponse.data?.curricula || []);
      try {
        const sessionRequest = `${Date.now()}-${requestVersion}`;
        await axios.get(`${API}/learn/session`, {
          params: { _learn_session: sessionRequest },
          withCredentials: true,
        });
        if (requestVersion !== learnRequestVersionRef.current) return;
        setIsLearnAuthenticated(true);
        try {
          const enrollmentResponse = await axios.get(`${API}/learn/me/enrollments`, {
            params: { _learn_session: sessionRequest },
            withCredentials: true,
          });
          if (requestVersion !== learnRequestVersionRef.current) return;
          setEnrollments(enrollmentResponse.data?.enrollments || []);
        } catch (_enrollmentLoadError) {
          if (requestVersion === learnRequestVersionRef.current) setEnrollments([]);
        }
      } catch (_ignored) {
        if (requestVersion === learnRequestVersionRef.current) clearPrivateLearnState();
      }
    } catch (loadError) {
      if (requestVersion === learnRequestVersionRef.current) {
        setError(loadError.response?.data?.detail || (activeLanguage === 'hi' ? 'कलामंच लर्न अभी लोड नहीं हो सका।' : 'Kalamanch Learn could not load right now.'));
      }
    } finally {
      if (requestVersion === learnRequestVersionRef.current) setLoading(false);
    }
  }, [activeLanguage, clearPrivateLearnState]);

  useEffect(() => {
    loadLearn();
  }, [loadLearn]);

  useEffect(() => {
    const focusPlans = async (event) => {
      learnRequestVersionRef.current += 1;
      clearPrivateLearnState();
      if (event?.detail?.authenticated === false) {
        setLoading(false);
        return;
      }
      await loadLearn();
      window.setTimeout(() => document.getElementById('learn-my-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    };
    window.addEventListener('learnAuthChanged', focusPlans);
    return () => window.removeEventListener('learnAuthChanged', focusPlans);
  }, [clearPrivateLearnState, loadLearn]);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    if (!loading && search.get('section') === 'my-plans') {
      window.setTimeout(() => document.getElementById('learn-my-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [loading, enrollments.length]);

  const filteredArtForms = useMemo(() => {
    const cleanQuery = query.trim().toLowerCase();
    return artForms.filter((form) => {
      const matchesQuery = !cleanQuery || [form.name, form.discipline, form.origin_context].filter(Boolean).join(' ').toLowerCase().includes(cleanQuery);
      return matchesQuery;
    });
  }, [artForms, query]);

  const groupedArtForms = useMemo(() => {
    return filteredArtForms.reduce((groups, form) => {
      const label = disciplineLabels[form.discipline] || 'Other';
      groups[label] = groups[label] || [];
      groups[label].push(form);
      return groups;
    }, {});
  }, [filteredArtForms]);

  const visibleCurricula = useMemo(() => {
    return curricula.filter((curriculum) => !selectedArtFormId || curriculum.art_form_id === selectedArtFormId);
  }, [curricula, selectedArtFormId]);

  const selectedCurriculumEnrollment = useMemo(() => {
    if (!selectedCurriculum?.id) return null;
    return enrollments.find((item) => item.curriculum_id === selectedCurriculum.id || item.curriculum?.id === selectedCurriculum.id) || null;
  }, [enrollments, selectedCurriculum?.id]);

  const selectCurriculum = async (curriculumId) => {
    setError('');
    setMessage('');
    setEnrollError('');
    setEnrollMessage('');
    try {
      const response = await axios.get(`${API}/learn/curricula/${encodeURIComponent(curriculumId)}`);
      setSelectedCurriculum(response.data?.curriculum || null);
      setSelectedPlan(null);
      window.setTimeout(() => document.getElementById('learn-preview')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (selectError) {
      setError(selectError.response?.data?.detail || 'Could not open this curriculum.');
    }
  };

  const enroll = async (event) => {
    event.preventDefault();
    if (!selectedCurriculum?.id) return;
    setEnrolling(true);
    setError('');
    setMessage('');
    setEnrollError('');
    setEnrollMessage('');
    try {
      const response = await axios.post(
        `${API}/learn/enroll`,
        {
          curriculum_id: selectedCurriculum.id,
          self_reported_level: enrollForm.self_reported_level,
          weekly_hours_committed: Number(enrollForm.weekly_hours_committed || 1),
          goal: enrollForm.goal,
        },
        { withCredentials: true }
      );
      const learnerCurriculumId = response.data?.enrollment?.learner_curriculum_id || response.data?.learner_curriculum?.id;
      setIsLearnAuthenticated(true);
      setEnrollMessage(response.data?.already_enrolled ? 'You are already enrolled. Opening your plan.' : 'Enrollment created. Opening your personal plan.');
      await loadLearn();
      if (learnerCurriculumId) {
        await openPlan(learnerCurriculumId);
      }
    } catch (enrollError) {
      const detail = enrollError.response?.data?.detail;
      const status = enrollError.response?.status;
      const validationMessage = Array.isArray(detail)
        ? detail.map((item) => item?.msg).filter(Boolean).join(' ')
        : '';
      if (status === 401) {
        setEnrollError('Your Learn session was not recognised. Please refresh once; you should not need to sign in again.');
      } else {
        setEnrollError(
          (typeof detail === 'string' && detail)
          || detail?.message
          || validationMessage
          || 'Enrollment could not be completed right now. Please try again.'
        );
      }
    } finally {
      setEnrolling(false);
    }
  };

  const openPlan = async (learnerCurriculumId, focusLessonId = '') => {
    const requestVersion = ++learnRequestVersionRef.current;
    setError('');
    setMessage('');
    setEnrollError('');
    setEnrollMessage('');
    try {
      const response = await axios.get(`${API}/learn/me/curricula/${encodeURIComponent(learnerCurriculumId)}`, {
        params: { language: activeLanguage },
        withCredentials: true,
      });
      if (requestVersion !== learnRequestVersionRef.current) return;
      setSelectedPlan(response.data?.learner_curriculum || null);
      setPlanProgress(response.data?.progress || []);
      setContentWatermark(response.data?.content_watermark?.label || '');
      setSelectedCurriculum(null);
      window.setTimeout(() => {
        const target = focusLessonId ? document.getElementById(`learn-lesson-${focusLessonId}`) : null;
        (target || document.getElementById('learn-plan'))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } catch (planError) {
      if (requestVersion === learnRequestVersionRef.current) {
        clearPrivateLearnState();
        setError(planError.response?.data?.detail || 'Could not open your plan.');
      }
    }
  };

  const markComplete = async (lessonId) => {
    setError('');
    setMessage('');
    try {
      await axios.post(`${API}/learn/me/lessons/${encodeURIComponent(lessonId)}/complete`, {}, { withCredentials: true });
      setMessage('Lesson marked complete.');
      if (selectedPlan?.id) {
        await openPlan(selectedPlan.id);
      }
    } catch (completeError) {
      setError(completeError.response?.data?.detail || 'Could not mark lesson complete.');
    }
  };

  const progressByLesson = useMemo(() => {
    return planProgress.reduce((map, item) => {
      if (item.lesson_id) map[item.lesson_id] = item;
      return map;
    }, {});
  }, [planProgress]);

  useEffect(() => {
    if (selectedPlan?.id) {
      openPlan(selectedPlan.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLanguage]);

  useEffect(() => {
    const loadLessonTargets = async () => {
      const artFormId = selectedPlan?.curriculum_snapshot?.art_form_id || selectedPlan?.art_form_id || '';
      if (!selectedPlan?.id || !artFormId) {
        setLessonTargets([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/learn/lesson-targets`, {
          params: { art_form_id: artFormId },
          withCredentials: true,
        });
        setLessonTargets(response.data?.lesson_targets || []);
      } catch (_err) {
        setLessonTargets([]);
      }
    };
    loadLessonTargets();
  }, [selectedPlan?.id, selectedPlan?.art_form_id, selectedPlan?.curriculum_snapshot?.art_form_id, activeLanguage]);

  const openFreeLearnerSignup = () => {
    window.dispatchEvent(new Event('openLearnerLogin'));
  };

  const focusEnrollmentForm = () => {
    document.getElementById('learn-enroll-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28">
      <SEO
        title={copy.seoTitle}
        description={copy.seoDescription}
      />

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-xl">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
            <div className="bg-gradient-to-br from-[#0F172A] via-[#4A148C] to-[#FF6F00] px-6 py-12 text-white sm:px-10">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-orange-200">{copy.heroEyebrow}</p>
              <h1 className="mt-4 text-4xl font-black leading-tight sm:text-6xl">{copy.heroTitle}</h1>
              <p className="mt-5 max-w-2xl text-base leading-8 text-orange-50">
                {copy.heroBody}
              </p>
            </div>
            <div className="bg-gradient-to-br from-white via-orange-50 to-emerald-50 px-6 py-12 sm:px-10">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-[#4A148C]">{copy.browseCourses}</p>
                <div className="rounded-full bg-white p-1 ring-1 ring-orange-100">
                  {['en', 'hi'].map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setLanguage(item)}
                      className={`rounded-full px-4 py-2 text-xs font-black ${activeLanguage === item ? 'bg-[#FF6F00] text-white' : 'text-slate-600 hover:bg-orange-50'}`}
                    >
                      {item === 'hi' ? 'हिंदी' : 'English'}
                    </button>
                  ))}
                </div>
              </div>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="mt-5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base outline-none focus:border-[#FF6F00]"
                placeholder={copy.searchPlaceholder}
              />
              <div className="mt-5 flex flex-wrap gap-3">
                <span className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-black text-emerald-700 ring-1 ring-emerald-100">
                  {loading ? copy.loading : `${artForms.length} ${copy.artForms}`}
                </span>
                <span className="rounded-full bg-orange-50 px-4 py-2 text-sm font-black text-[#C45A00] ring-1 ring-orange-100">
                  {copy.generatedPublished}
                </span>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{typeof error === 'string' ? error : error?.message || 'Something went wrong.'}</div>}
        {message && <div className="mt-6 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{message}</div>}

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="rounded-3xl border border-orange-100 bg-white p-5 shadow-lg">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">{copy.artFormBrowser}</p>
            <div className="mt-5 space-y-5">
              {Object.entries(groupedArtForms).map(([discipline, forms]) => (
                <div key={discipline}>
                  <h2 className="text-lg font-black text-slate-950">{discipline}</h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {forms.map((form) => (
                      <button
                        key={form.id}
                        type="button"
                        onClick={() => setSelectedArtFormId(form.id)}
                        className={`rounded-full border px-4 py-2 text-sm font-bold transition ${selectedArtFormId === form.id ? 'border-[#FF6F00] bg-[#FF6F00] text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-orange-200 hover:bg-orange-50'}`}
                      >
                        {form.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-purple-100 bg-white p-5 shadow-lg">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">{copy.publishedCurricula}</p>
            <div className="mt-5 space-y-4">
              {visibleCurricula.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 p-5 font-semibold text-slate-500">
                  {copy.noCurriculum}
                </div>
              ) : (
                visibleCurricula.map((curriculum) => (
                  <article key={curriculum.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6F00]">{curriculum.art_form_name}</p>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${courseTier(curriculum) === 'coached' ? 'bg-purple-100 text-purple-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {courseLabel(curriculum)}
                      </span>
                      {courseBadge(curriculum) && <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-amber-800">{courseBadge(curriculum)}</span>}
                    </div>
                    {activeLanguage === 'hi' && (curriculum.language || 'en') !== 'hi' && (
                      <p className="mt-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-black text-[#C45A00] ring-1 ring-orange-100">{copy.englishFallback}</p>
                    )}
                    <h3 className="mt-2 text-xl font-black text-slate-950">{curriculum.title}</h3>
                    <MarkdownText className="mt-2 text-sm text-slate-600">{curriculum.summary}</MarkdownText>
                    <p className="mt-3 rounded-xl bg-white px-3 py-2 text-xs font-bold leading-5 text-slate-700">{courseNotice(curriculum)}</p>
                    <div className="mt-4 flex flex-wrap gap-2 text-xs font-black">
                      <span className="rounded-full bg-white px-3 py-1 text-slate-700">{curriculum.total_levels} levels</span>
                      {courseTier(curriculum) !== 'guide' && <span className="rounded-full bg-white px-3 py-1 text-slate-700">{curriculum.estimated_total_hours} {courseHoursLabel(curriculum)}</span>}
                    </div>
                    <button type="button" className="mt-4 rounded-full bg-[#FF6F00] px-5 py-2 text-sm font-black text-white" onClick={() => selectCurriculum(curriculum.id)}>
                      {copy.previewSyllabus}
                    </button>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>

        {enrollments.length > 0 && (
          <section id="learn-my-plans" className="mt-8 rounded-3xl border border-emerald-100 bg-white p-5 shadow-lg sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">{copy.learnerDashboard}</p>
                <h2 className="mt-2 text-2xl font-black text-slate-950">{copy.continueWhereLeft}</h2>
                <p className="mt-1 text-sm font-semibold text-slate-600">{copy.dashboardHint}</p>
              </div>
              <span className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-black text-emerald-700 ring-1 ring-emerald-100">{enrollments.length} {copy.plans}</span>
            </div>
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              {enrollments.map((item) => {
                const percent = enrollmentProgressPercent(item);
                const completed = Number(item.progress_summary?.completed_lessons || 0);
                const total = Number(item.progress_summary?.total_lessons || 0);
                const nextLesson = item.progress_summary?.next_lesson;
                return (
                  <article key={item.id} className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">{item.status}</p>
                        <h3 className="mt-2 text-lg font-black text-slate-950">{item.curriculum?.title || copy.learnPlan}</h3>
                        <p className="mt-1 text-sm font-semibold text-slate-600">{item.curriculum?.art_form_name || ''}</p>
                        <p className="mt-2 text-xs font-black uppercase tracking-[0.14em] text-emerald-700">{courseLabel(item.curriculum)}</p>
                        {courseBadge(item.curriculum) && <span className="mt-2 inline-flex rounded-full bg-amber-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-amber-800">{courseBadge(item.curriculum)}</span>}
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-emerald-700 ring-1 ring-emerald-100">{percent}% {copy.done}</span>
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-white ring-1 ring-emerald-100">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${percent}%` }} />
                    </div>
                    <p className="mt-3 text-sm font-bold text-slate-700">
                      {total ? `${completed} of ${total} lessons complete` : copy.progressWillAppear}
                    </p>
                    {nextLesson ? (
                      <p className="mt-2 text-sm font-semibold text-slate-600">
                        Next: {copy.level} {nextLesson.level_number}, Lesson {nextLesson.lesson_number} - {nextLesson.lesson_title}
                      </p>
                    ) : (
                      <p className="mt-2 text-sm font-semibold text-emerald-700">{copy.allComplete}</p>
                    )}
                    <button type="button" onClick={() => openPlan(item.learner_curriculum_id, nextLesson?.lesson_id)} className="mt-4 rounded-full bg-[#FF6F00] px-5 py-2 text-sm font-black text-white">
                      {copy.continuePlan}
                    </button>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {selectedCurriculum && (
          <section id="learn-preview" className="mt-8 rounded-3xl border border-orange-100 bg-white p-5 shadow-lg sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">{copy.curriculumPreview}</p>
            {activeLanguage === 'hi' && (selectedCurriculum.language || 'en') !== 'hi' && (
              <p className="mt-2 inline-flex rounded-full bg-orange-50 px-3 py-1 text-xs font-black text-[#C45A00] ring-1 ring-orange-100">{copy.englishFallback}</p>
            )}
            <h2 className="mt-2 text-3xl font-black text-slate-950">{selectedCurriculum.title}</h2>
            <MarkdownText className="mt-3 text-slate-600">{selectedCurriculum.summary}</MarkdownText>
            <div className={`mt-4 rounded-2xl border p-4 text-sm font-bold ${courseTier(selectedCurriculum) === 'coached' ? 'border-purple-100 bg-purple-50 text-purple-900' : 'border-emerald-100 bg-emerald-50 text-emerald-900'}`}>
              <span className="mr-2 font-black uppercase tracking-[0.12em]">{courseLabel(selectedCurriculum)}.</span>
              {courseBadge(selectedCurriculum) && <span className="mr-2 inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-black uppercase tracking-[0.1em] text-amber-800">{courseBadge(selectedCurriculum)}</span>}
              {courseNotice(selectedCurriculum)}
            </div>
            <div className="mt-4 rounded-2xl border border-purple-100 bg-purple-50 p-4 text-sm font-bold text-purple-900">{trustDisclosure}</div>
            {courseTier(selectedCurriculum) === 'coming_soon' && (
              <ComingSoonInterestPanel curriculum={selectedCurriculum} isAuthenticated={isLearnAuthenticated} />
            )}
            <form id="learn-enroll-form" className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 p-4" onSubmit={enroll}>
              <h3 className="text-xl font-black text-slate-950">Enroll in this {courseTier(selectedCurriculum) === 'coached' ? 'coached course' : courseTier(selectedCurriculum) === 'coming_soon' ? 'course' : 'practice guide'}</h3>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <select className="rounded-2xl border border-slate-200 px-4 py-3" value={enrollForm.self_reported_level} onChange={(event) => setEnrollForm((prev) => ({ ...prev, self_reported_level: event.target.value }))}>
                  {Object.entries(activeLevelLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <input className="rounded-2xl border border-slate-200 px-4 py-3" type="number" min="1" max="40" value={enrollForm.weekly_hours_committed} onChange={(event) => setEnrollForm((prev) => ({ ...prev, weekly_hours_committed: event.target.value }))} placeholder={copy.hoursPerWeek} />
                <button className="rounded-full bg-[#FF6F00] px-5 py-3 font-black text-white disabled:cursor-not-allowed disabled:opacity-60" type="submit" disabled={enrolling}>
                  {enrolling ? copy.enrolling : copy.enroll}
                </button>
              </div>
              {enrollError && (
                <div className="mt-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                  {typeof enrollError === 'string' ? enrollError : enrollError?.message || 'Please sign in before enrolling.'}
                </div>
              )}
              {enrollMessage && (
                <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">
                  {enrollMessage}
                </div>
              )}
              <textarea className="mt-3 min-h-24 w-full rounded-2xl border border-slate-200 px-4 py-3" value={enrollForm.goal} onChange={(event) => setEnrollForm((prev) => ({ ...prev, goal: event.target.value }))} placeholder={copy.optionalGoal} />
            </form>
            <div className="mt-6 space-y-4">
              {selectedCurriculum.levels?.map((level) => {
                const levelNumber = Number(level.level_number || 0);
                const isPublicLevel = levelNumber <= 1;
                return (
                  <div key={level.id || level.level_number}>
                    {levelNumber === 2 && (
                      <div className="mb-4 rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm">
                        <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">
                          {isLearnAuthenticated ? (selectedCurriculumEnrollment ? 'Your plan is ready' : 'Free account active') : copy.freeContinueTitle}
                        </p>
                        <h3 className="mt-2 text-2xl font-black text-slate-950">
                          {isLearnAuthenticated ? (selectedCurriculumEnrollment ? 'Continue with this course' : 'Enroll free to continue') : copy.signUpFree}
                        </h3>
                        <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-600">
                          {isLearnAuthenticated
                            ? (selectedCurriculumEnrollment
                              ? 'Open your personal plan to continue with Level 2 and keep your progress saved.'
                              : 'You are signed in. Enroll in this course to unlock Level 2 and save your progress.')
                            : copy.freeContinueBody}
                        </p>
                        <button
                          type="button"
                          onClick={selectedCurriculumEnrollment
                            ? () => openPlan(selectedCurriculumEnrollment.learner_curriculum_id)
                            : isLearnAuthenticated ? focusEnrollmentForm : openFreeLearnerSignup}
                          className="mt-4 rounded-full bg-[#FF6F00] px-5 py-3 text-sm font-black text-white"
                        >
                          {isLearnAuthenticated ? (selectedCurriculumEnrollment ? copy.continuePlan : 'Enroll free') : copy.signUpFree}
                        </button>
                      </div>
                    )}
                    <details className="rounded-2xl border border-slate-100 bg-white p-4" open={isPublicLevel}>
                      <summary className="cursor-pointer text-lg font-black text-slate-950">{copy.level} {level.level_number}: {level.title}</summary>
                      {isPublicLevel && <p className="mt-3 text-sm leading-6 text-slate-600">{level.objective}</p>}
                      {isPublicLevel && (
                        <div className="mt-3 flex flex-wrap gap-2 text-sm font-bold text-slate-700">
                          {courseTier(selectedCurriculum) !== 'guide' && <span className="rounded-full bg-slate-50 px-3 py-1">{level.estimated_hours} {courseHoursLabel(selectedCurriculum)}</span>}
                          <span className="rounded-full bg-slate-50 px-3 py-1">{level.estimated_weeks} weeks</span>
                        </div>
                      )}
                      {isPublicLevel && Array.isArray(level.key_skills) && level.key_skills.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {level.key_skills.slice(0, 8).map((skill) => (
                            <span key={skill} className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black text-[#C45A00] ring-1 ring-orange-100">{skill}</span>
                          ))}
                        </div>
                      )}
                      {isPublicLevel ? (
                        <div className="mt-4 space-y-2">
                          {level.lessons?.map((lesson) => (
                            <div key={lesson.id} className="rounded-xl bg-slate-50 p-3">
                              <p className="font-black text-slate-950">{lesson.lesson_number}. {lesson.title}</p>
                              <MarkdownText className="mt-1 text-sm text-slate-600">{lesson.instruction_md}</MarkdownText>
                              {lesson.drill_md && <MarkdownText className="mt-2 text-sm font-semibold text-[#4A148C]">{lesson.drill_md}</MarkdownText>}
                              <LessonDiagram svg={lesson.diagram_svg} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold leading-6 text-emerald-900">
                          {Number(level.lesson_count || 0) > 0 && <p className="mb-2">{level.lesson_count} lessons</p>}
                          {copy.levelLockedHint}
                        </div>
                      )}
                    </details>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {selectedPlan && (
          <section id="learn-plan" className="relative mt-8 overflow-hidden rounded-3xl border border-emerald-100 bg-white p-5 shadow-lg sm:p-8">
            <ContentWatermark label={contentWatermark} />
            <div className="relative z-10">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">{copy.myPlan} · {courseLabel(selectedPlan.curriculum_snapshot)}</p>
            {courseBadge(selectedPlan.curriculum_snapshot) && <span className="mt-3 inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-black uppercase tracking-[0.1em] text-amber-800">{courseBadge(selectedPlan.curriculum_snapshot)}</span>}
            <h2 className="mt-2 text-3xl font-black text-slate-950">{selectedPlan.curriculum_snapshot?.title}</h2>
            <p className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-900">{courseNotice(selectedPlan.curriculum_snapshot)}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {planSummaryItems(selectedPlan, copy, activeLanguage).map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">{label}</p>
                  <p className="mt-2 text-lg font-black text-slate-950">{value}</p>
                </div>
              ))}
            </div>
            {shortRationale(selectedPlan.plan_rationale) && (
              <p className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-bold leading-6 text-slate-700">{shortRationale(selectedPlan.plan_rationale)}</p>
            )}
            <div className="mt-4 rounded-2xl border border-purple-100 bg-purple-50 p-4 text-sm font-bold text-purple-900">{trustDisclosure}</div>
            {courseTier(selectedPlan.curriculum_snapshot) === 'coming_soon' && (
              <ComingSoonInterestPanel
                curriculum={{ ...selectedPlan.curriculum_snapshot, id: selectedPlan.curriculum_id }}
                isAuthenticated
              />
            )}
            <div className="mt-6 space-y-4">
              {selectedPlan.curriculum_snapshot?.levels?.filter((level) => level.level_number >= selectedPlan.starting_level_number).map((level) => (
                <div key={level.id} className="rounded-2xl border border-slate-100 bg-white p-4">
                  <h3 className="text-xl font-black text-slate-950">{copy.level} {level.level_number}: {level.title}</h3>
                  <div className="mt-3 grid gap-3">
                    {level.lessons?.map((lesson) => {
                      const progress = progressByLesson[lesson.id];
                      const done = ['self_marked_complete', 'verified_complete'].includes(progress?.state);
                      const linkedLessonTargets = lessonTargets.filter((target) => target.lesson_id === lesson.id);
                      return (
                        <article id={`learn-lesson-${lesson.id}`} key={lesson.id} className="rounded-xl bg-slate-50 p-4">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="font-black text-slate-950">{lesson.lesson_number}. {lesson.title}</p>
                              <MarkdownText className="mt-2 text-sm text-slate-600">{lesson.instruction_md}</MarkdownText>
                              {lesson.drill_md && <MarkdownText className="mt-2 text-sm font-semibold text-[#4A148C]">{lesson.drill_md}</MarkdownText>}
                              <LessonDiagram svg={lesson.diagram_svg} />
                            </div>
                            <button type="button" onClick={() => markComplete(lesson.id)} disabled={done} className={`rounded-full px-4 py-2 text-sm font-black ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-[#FF6F00] text-white'}`}>
                              {done ? copy.complete : copy.markComplete}
                            </button>
                          </div>
                          {courseTier(selectedPlan.curriculum_snapshot) === 'coached' && linkedLessonTargets.length > 0 && (
                            <VoicePracticePanel
                              lesson={lesson}
                              selectedPlan={selectedPlan}
                              lessonTargets={linkedLessonTargets}
                              activeLanguage={activeLanguage}
                            />
                          )}
                        </article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            </div>
          </section>
        )}
      </section>
    </main>
  );
}
